import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArticleReader } from "@/components/ArticleReader";
import { ArticleEditor, ResourceEditor } from "@/components/LearnEditors";
import { BookOpen, ExternalLink, Sparkles, Clock, CheckCircle2, Plus, Pencil, Trash2, Link2 } from "@/lib/icons";

export default function Learn() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const isClinician = user?.role === "clinician";
  const [data, setData] = useState({ articles: [], resources: [], suggested: [], read_count: 0, categories: [] });
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [editing, setEditing] = useState(null); // {type:'article'|'resource', item?}
  const [params, setParams] = useSearchParams();
  const wantedRef = useRef(params.get("article"));

  const load = useCallback(async () => {
    const r = await api.get("/learn");
    setData(r.data);
    const wanted = wantedRef.current;
    wantedRef.current = null;
    if (wanted) {
      const a = r.data.articles.find((x) => x.id === wanted);
      if (a) setOpen(a);
      setParams({}, { replace: true });
    }
  }, [setParams]);
  useEffect(() => { load(); }, [load]);

  const markRead = async (article) => {
    const r = await api.post(`/learn/articles/${article.id}/read`);
    if (r.data.already) toast("Already counted — re-reading is still a win.");
    else toast.success(`Nice. +${r.data.points_earned} pts for learning.`);
    (r.data.achievements || []).forEach((a) => toast.success(`Achievement unlocked: ${a.title} (+${a.points})`, { duration: 5000 }));
    await load();
    setOpen({ ...article, read: true });
  };

  const save = async (payload) => {
    try {
      const path = editing.type === "article" ? "/learn/articles" : "/learn/resources";
      if (editing.item) await api.put(`${path}/${editing.item.id}`, payload);
      else await api.post(path, payload);
      toast.success(editing.item ? "Updated." : "Published.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (type, id) => {
    await api.delete(`/learn/${type}/${id}`);
    toast("Removed from the library.");
    load();
  };

  const articles = data.articles.filter((a) => filter === "all" || a.category === filter);
  const resources = data.resources.filter((r) => filter === "all" || r.category === filter);
  const byId = Object.fromEntries(data.articles.map((a) => [a.id, a]));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-8 relative z-10">
      {open && <ArticleReader article={open} onClose={() => setOpen(null)} onRead={markRead} isClient={isClient} />}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Learn</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-stone-900 mt-1">The science of sticking with it.</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            Short, evidence-based reads on forming and keeping habits — with links to trusted sources if you want to go deeper.
          </p>
        </div>
        {isClient && (
          <div className="warm-card px-5 py-3 text-center" data-testid="read-count">
            <div className="font-mono text-2xl font-bold text-amber-800">{data.read_count}</div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">articles read</div>
          </div>
        )}
        {isClinician && (
          <div className="flex gap-2">
            <button data-testid="new-article-btn" onClick={() => setEditing({ type: "article" })} className="amber-btn text-sm inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Article
            </button>
            <button data-testid="new-resource-btn" onClick={() => setEditing({ type: "resource" })} className="px-4 py-2 rounded-full text-sm font-semibold bg-stone-900 text-white inline-flex items-center gap-1.5">
              <Link2 className="w-4 h-4" /> External link
            </button>
          </div>
        )}
      </div>

      {editing && (editing.type === "article"
        ? <ArticleEditor initial={editing.item} onSave={save} onCancel={() => setEditing(null)} />
        : <ResourceEditor initial={editing.item} onSave={save} onCancel={() => setEditing(null)} />)}

      {isClient && data.suggested.length > 0 && (
        <div className="rounded-3xl p-6 bg-gradient-to-br from-amber-100 via-orange-50 to-emerald-50 border border-amber-200/70" data-testid="suggested-section">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-800 mb-4">
            <Sparkles className="w-4 h-4" /> Suggested for you
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {data.suggested.map((s) => {
              const a = byId[s.article_id];
              if (!a) return null;
              return (
                <button key={a.id} data-testid={`suggested-${a.id}`} onClick={() => setOpen(a)} className="warm-card p-4 text-left hover:-translate-y-0.5 transition-transform">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">{s.why}</div>
                  <div className="font-display font-bold text-stone-900 mt-1 leading-snug">{a.title}</div>
                  <div className="text-xs text-stone-500 mt-1 line-clamp-2">{a.summary}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {["all", ...data.categories].map((c) => (
          <button key={c} data-testid={`learn-filter-${c}`} onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition ${filter === c ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center gap-2 text-stone-800">
            <BookOpen className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Articles ({articles.length})</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {articles.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className={`warm-card p-5 flex flex-col ${a.read ? "border-emerald-200" : ""}`} data-testid={`article-card-${a.id}`}>
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-amber-800">
                  <span>{a.category}</span>
                  <span className="inline-flex items-center gap-1 text-stone-500"><Clock className="w-3 h-3" /> {a.read_minutes} min</span>
                </div>
                <h3 className="font-display font-bold text-stone-900 mt-2 leading-snug">{a.title}</h3>
                <p className="text-sm text-stone-600 mt-1.5 flex-1">{a.summary}</p>
                <div className="flex items-center justify-between mt-4 gap-2">
                  <button data-testid={`article-open-${a.id}`} onClick={() => setOpen(a)} className="text-sm font-semibold text-stone-900 hover:text-amber-800">Read →</button>
                  <div className="flex items-center gap-2">
                    {a.read && <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Read</span>}
                    {a.author_type === "clinician" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">From {a.editable ? "you" : a.author_name}</span>}
                    {a.editable && (
                      <>
                        <button data-testid={`article-edit-${a.id}`} onClick={() => setEditing({ type: "article", item: a })} className="text-stone-400 hover:text-stone-800"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`article-delete-${a.id}`} onClick={() => remove("articles", a.id)} className="text-stone-400 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-4 space-y-4">
          <div className="flex items-center gap-2 text-stone-800">
            <ExternalLink className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">External resources ({resources.length})</span>
          </div>
          <div className="warm-card divide-y divide-stone-100">
            {resources.map((r) => (
              <div key={r.id} className="p-4" data-testid={`resource-${r.id}`}>
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-stone-900 hover:text-amber-800 inline-flex items-center gap-1.5">
                  {r.title} <ExternalLink className="w-3.5 h-3.5 text-stone-400" />
                </a>
                <p className="text-xs text-stone-500 mt-1">{r.description}</p>
                <div className="flex items-center gap-2 mt-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
                  <span>{r.category}</span>
                  {r.author_type === "clinician" && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 normal-case tracking-normal">From {r.editable ? "you" : r.author_name}</span>}
                  {r.editable && (
                    <>
                      <button data-testid={`resource-edit-${r.id}`} onClick={() => setEditing({ type: "resource", item: r })} className="text-stone-400 hover:text-stone-800"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`resource-delete-${r.id}`} onClick={() => remove("resources", r.id)} className="text-stone-400 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {resources.length === 0 && <div className="p-4 text-sm text-stone-500">No links in this category yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
